import Model from './model';

/**
 * @model CultureSummaryReportModel
 * @description
 * Represents the data structure for a Culture Summary Report within the AquaCloud application.
 * This model acts as a comprehensive Data Transfer Object (DTO) for aggregated performance metrics
 * of a specific aquaculture cycle (culture) in a pond.
 *
 * **Domain Context:**
 * A "Culture" represents a lifecycle of aquatic animals from stocking to harvest in a specific pond.
 * Evaluating the success of a culture involves analyzing biological metrics (Survival, Growth)
 * and financial metrics (Cost of Production).
 *
 * **Purpose:**
 * To provide a flattened, report-ready structure that combines data from various sources
 * (Feed Logs, Harvest Records, Expense Logs) into a single view for analysis.
 *
 * **Key Responsibilities:**
 * 1. **Identification**: Links the report to specific `PondID`, `PondCultureID`, and `farm_site_id`.
 * 2. **Biological Metrics**: Stores calculated values for `SurvivalRate`, `abw` (Average Body Weight), `fcr` (Feed Conversion Ratio), and `TBM` (Total Biomass).
 * 3. **Financial Metrics**: Tracks `total_pond_spend` and unit costs (`production_cost_per_kg`).
 * 4. **Operational Context**: Includes details like `stocking_count`, `density`, and `Hatcheryname`.
 *
 * **Data Flow:**
 * - **Generation**: Calculated by the backend (likely an aggregation pipeline) based on historical logs.
 * - **Retrieval**: Fetched via `CultureSummaryReportService` for display in dashboards or export to Excel/PDF.
 * - **Usage**: Used by Farm Managers to audit past performance and plan future cycles.
 *
 * **User Roles & Permissions:**
 * - **Farm Manager/Owner**: View financial and performance summaries.
 * - **Technician**: View biological metrics.
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class CultureSummaryReportModel extends Model {
	/**
	 * @type {string}
	 * @description Name of the file if the report was generated or exported.
	 * @optional
	 */
	filename?: string;

	/**
	 * @type {string}
	 * @description Display name of the report or the entity being reported on.
	 * @optional
	 */
	name?: string;

	/**
	 * @type {number}
	 * @description Unique identifier of the pond.
	 * @optional
	 */
	PondID?: number;

	/**
	 * @type {number}
	 * @description Unique identifier of the specific culture cycle.
	 * @optional
	 */
	PondCultureID?: number;

	/**
	 * @type {string}
	 * @description A human-readable identifier or code for the culture (e.g., "C-2023-001").
	 * @optional
	 */
	culture_identifier_x?: string;

	/**
	 * @type {string}
	 * @description Name of the section within the farm where the pond is located.
	 * @optional
	 */
	section_name?: string;

	/**
	 * @type {string}
	 * @description Unique identifier of the farm site.
	 * @optional
	 */
	farm_site_id?: string;

	/**
	 * @type {Date}
	 * @description The date when the culture started (stocking date).
	 * @optional
	 */
	culture_start_date?: Date;

	/**
	 * @type {Date}
	 * @description The date when the culture ended (harvest date).
	 * @optional
	 */
	culture_end_date?: Date;

	/**
	 * @type {number}
	 * @description The duration of the culture in days (Days of Culture - DOC).
	 * @optional
	 */
	Age_of_culture?: number;

	/**
	 * @type {string}
	 * @description Name of the hatchery that supplied the seed/post-larvae.
	 * @optional
	 */
	Hatcheryname?: string;

	/**
	 * @type {number}
	 * @description The initial number of animals stocked in the pond.
	 * @optional
	 */
	stocking_count?: number;

	/**
	 * @type {number}
	 * @description The size of the pond (usually in hectares or acres).
	 * @optional
	 */
	pond_size?: number;

	/**
	 * @type {number}
	 * @description Stocking density (animals per unit area).
	 * @optional
	 */
	density?: number;

	/**
	 * @type {number}
	 * @description Total weight harvested from the pond (in kg).
	 * @optional
	 */
	harvest_weight?: number;

	/**
	 * @type {string}
	 * @description The method or mode of harvest (e.g., "Partial", "Full").
	 * @optional
	 */
	harvest_mode?: string;

	/**
	 * @type {number}
	 * @description The estimated or actual number of animals remaining/harvested.
	 * @optional
	 */
	no_of_animals?: number;

	/**
	 * @type {number}
	 * @description The percentage of animals that survived until harvest.
	 * Formula: (Harvest Count / Stocking Count) * 100.
	 * @optional
	 */
	SurvivalRate?: number;

	/**
	 * @type {number}
	 * @description Average Body Weight (ABW) of the animals at harvest (in grams).
	 * @optional
	 */
	abw?: number;

	/**
	 * @type {number}
	 * @description Count of animals (context-dependent, usually current standing stock).
	 * @optional
	 */
	count?: number;

	/**
	 * @type {number}
	 * @description Cumulative Feed (CF) - Total feed consumed during the cycle (in kg).
	 * @optional
	 */
	cf?: number;

	/**
	 * @type {number}
	 * @description Feed Conversion Ratio (FCR).
	 * Formula: Total Feed Given / Total Biomass Gained.
	 * Lower is better.
	 * @optional
	 */
	fcr?: number;

	/**
	 * @type {number}
	 * @description Total Biomass (TBM) estimated or realized (in kg).
	 * @optional
	 */
	TBM?: number;

	/**
	 * @type {number}
	 * @description Total operational expenditure for this specific pond culture.
	 * @optional
	 */
	total_pond_spend?: number;

	/**
	 * @type {number}
	 * @description Cost of production per kilogram of biomass.
	 * Critical financial metric.
	 * @optional
	 */
	production_cost_per_kg?: number;

	/**
	 * @type {number}
	 * @description Cost of production per individual animal (piece).
	 * @optional
	 */
	productioncostperpiece?: number;

	/**
	 * @type {string}
	 * @description Description of how the pond is used (e.g., "Growout", "Nursery").
	 * @optional
	 */
	ponduse?: string;

	/**
	 * @type {string}
	 * @description Unique identifier of the business entity owning the culture.
	 * @optional
	 */
	business_id?: string;

	/**
	 * @type {string}
	 * @description Name of the file associated with this report record.
	 */
	file_name: string;

	/**
	 * @type {string}
	 * @description ID of the person who generated or is responsible for this report.
	 */
  person_id: string;

	/**
	 * @constructor
	 * @description
	 * Initializes a new instance of the CultureSummaryReportModel.
	 * Calls the parent `Model` constructor.
	 */
	constructor() {
		super();
	}
}
